/**
 * Customer-facing digital download page.
 *
 * Design intent: a real product-delivery page, not a marketing hero.
 * Restrained, single-column, uses the store's own tokens where
 * available. The previous version leaned on a purple→violet gradient
 * hero (`#4f46e5 → #7c3aed`) that clashed with the Revstar brand teal
 * and read as AI-generated template chrome. This is the pared-back
 * replacement.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, Key, Clock, AlertCircle, CheckCircle, XCircle, Copy as CopyIcon } from 'lucide-react';

const BRAND = '#0d7463';
const BRAND_TINT = '#e8f5f2';
const INK = '#111827';
const MUTED = '#6b7280';
const HAIRLINE = '#e5e7eb';

const DigitalDownload = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) fetchDownloadInfo();
  }, [token]);

  const fetchDownloadInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/digital/access/${token}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.error || 'Invalid download link');
        return;
      }
      setDownloadInfo(data.fulfillment);
    } catch (err) {
      setError('Failed to load download information');
      console.error('Error fetching download info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadInfo?.canDownload) return;
    try {
      setDownloading(true);
      setError(null);
      const response = await fetch(`/api/digital/download/${token}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.error || 'Download failed');
        return;
      }
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
        setDownloadSuccess(true);
        await fetchDownloadInfo();
      }
    } catch (err) {
      setError('Failed to process download');
      console.error('Error processing download:', err);
    } finally {
      setDownloading(false);
    }
  };

  const copyLicenseKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.centered}>
          <div style={styles.spinner} />
          <p style={{ color: MUTED, marginTop: 12, fontSize: 14 }}>Loading…</p>
        </div>
        <style>{keyframes}</style>
      </div>
    );
  }

  if (error && !downloadInfo) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.errorIconWrap}>
            <XCircle style={{ width: 28, height: 28, color: '#dc2626' }} />
          </div>
          <h1 style={styles.errorTitle}>Download unavailable</h1>
          <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.5 }}>{error}</p>
          <Link to="/login" style={styles.linkBtn}>Sign in to your account</Link>
        </div>
      </div>
    );
  }

  const downloadsLeft = downloadInfo?.maxDownloads != null
    ? Math.max(0, downloadInfo.maxDownloads - (downloadInfo.downloadCount || 0))
    : null;
  const expiryLabel = downloadInfo?.expiresAt
    ? (downloadInfo.isExpired
        ? 'Link expired'
        : `Available until ${new Date(downloadInfo.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`)
    : null;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Product */}
        <div style={styles.productBlock}>
          {downloadInfo?.productImage && (
            <img
              src={downloadInfo.productImage}
              alt={downloadInfo.productName}
              style={styles.productImage}
            />
          )}
          <h1 style={styles.productName}>{downloadInfo?.productName}</h1>
          <p style={styles.productSubtitle}>Your digital purchase is ready.</p>
        </div>

        {/* License key */}
        {downloadInfo?.licenseKey && (
          <div style={styles.licenseBlock}>
            <div style={styles.licenseHeader}>
              <Key style={{ width: 14, height: 14, color: BRAND }} />
              <span style={styles.licenseLabel}>License key</span>
            </div>
            <div style={styles.licenseRow}>
              <code style={styles.licenseCode}>{downloadInfo.licenseKey}</code>
              <button
                onClick={() => copyLicenseKey(downloadInfo.licenseKey)}
                style={styles.copyBtn}
                aria-label="Copy license key"
              >
                <CopyIcon style={{ width: 14, height: 14 }} />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}

        {/* Meta line — downloads left + expiry */}
        {(downloadsLeft != null || expiryLabel) && (
          <div style={styles.metaRow}>
            {downloadsLeft != null && (
              <span style={styles.metaItem}>
                {downloadsLeft} download{downloadsLeft === 1 ? '' : 's'} left
              </span>
            )}
            {expiryLabel && (
              <>
                {downloadsLeft != null && <span style={styles.metaDot}>·</span>}
                <span style={styles.metaItem}>
                  <Clock style={{ width: 12, height: 12, marginRight: 4, verticalAlign: '-2px' }} />
                  {expiryLabel}
                </span>
              </>
            )}
          </div>
        )}

        {/* Inline error (post-load) */}
        {error && (
          <div style={styles.inlineError}>
            <AlertCircle style={{ width: 16, height: 16 }} />
            <span>{error}</span>
          </div>
        )}

        {/* Success */}
        {downloadSuccess && (
          <div style={styles.inlineSuccess}>
            <CheckCircle style={{ width: 16, height: 16 }} />
            <span>Download started — check your browser's downloads.</span>
          </div>
        )}

        {/* Primary action */}
        {downloadInfo?.canDownload ? (
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{
              ...styles.downloadBtn,
              opacity: downloading ? 0.7 : 1,
              cursor: downloading ? 'not-allowed' : 'pointer',
            }}
          >
            {downloading ? (
              <>
                <div style={styles.btnSpinner} />
                <span>Processing…</span>
              </>
            ) : (
              <>
                <Download style={{ width: 18, height: 18 }} />
                <span>Download</span>
              </>
            )}
          </button>
        ) : (
          <div style={styles.unavailable}>
            {downloadInfo?.isExpired && 'This download link has expired.'}
            {downloadInfo?.limitReached && 'You\'ve reached the download limit for this order.'}
            {!downloadInfo?.isExpired && !downloadInfo?.limitReached && !downloadInfo?.isActive && 'This download has been revoked.'}
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          Need help? <Link to="/login" style={styles.footerLink}>Sign in</Link> to view all your digital purchases.
        </div>
      </div>

      <style>{keyframes}</style>
    </div>
  );
};

const keyframes = `
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '48px 20px 24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    color: INK,
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '20vh',
  },
  container: {
    width: '100%',
    maxWidth: 420,
  },
  spinner: {
    width: 28,
    height: 28,
    border: '3px solid ' + HAIRLINE,
    borderTop: '3px solid ' + BRAND,
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  btnSpinner: {
    width: 16,
    height: 16,
    border: '2px solid rgba(255,255,255,0.4)',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },

  // Product
  productBlock: {
    textAlign: 'center',
    marginBottom: 28,
  },
  productImage: {
    width: '100%',
    maxWidth: 220,
    height: 'auto',
    aspectRatio: '3 / 4',
    objectFit: 'contain',
    borderRadius: 8,
    marginBottom: 20,
  },
  productName: {
    fontSize: 22,
    fontWeight: 600,
    color: INK,
    margin: '0 0 4px',
    letterSpacing: '-0.01em',
  },
  productSubtitle: {
    fontSize: 14,
    color: MUTED,
    margin: 0,
  },

  // License
  licenseBlock: {
    border: '1px solid ' + HAIRLINE,
    borderRadius: 10,
    padding: '14px 14px 12px',
    marginBottom: 16,
    backgroundColor: '#fafafa',
  },
  licenseHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  licenseLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: BRAND,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  licenseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  licenseCode: {
    flex: 1,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 14,
    color: INK,
    backgroundColor: '#ffffff',
    border: '1px solid ' + HAIRLINE,
    borderRadius: 6,
    padding: '8px 10px',
    wordBreak: 'break-all',
  },
  copyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 500,
    color: BRAND,
    backgroundColor: '#ffffff',
    border: '1px solid ' + HAIRLINE,
    borderRadius: 6,
    cursor: 'pointer',
  },

  // Meta
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 12,
    color: MUTED,
    marginBottom: 20,
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  metaDot: {
    color: '#d1d5db',
  },

  // Inline banners
  inlineError: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    fontSize: 13,
    marginBottom: 12,
  },
  inlineSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: BRAND_TINT,
    color: '#0f5b52',
    fontSize: 13,
    marginBottom: 12,
  },

  // CTA
  downloadBtn: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: BRAND,
    color: '#ffffff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  unavailable: {
    textAlign: 'center',
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
    fontSize: 14,
  },

  // Footer
  footer: {
    marginTop: 32,
    paddingTop: 20,
    borderTop: '1px solid ' + HAIRLINE,
    textAlign: 'center',
    fontSize: 13,
    color: MUTED,
  },
  footerLink: {
    color: BRAND,
    textDecoration: 'none',
    fontWeight: 500,
  },

  // Error page (no downloadInfo)
  errorIconWrap: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: '#fef2f2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '80px auto 16px',
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: INK,
    textAlign: 'center',
    margin: '0 0 8px',
  },
  linkBtn: {
    display: 'inline-block',
    marginTop: 20,
    padding: '10px 18px',
    backgroundColor: BRAND,
    color: '#ffffff',
    borderRadius: 8,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
  },
};

export default DigitalDownload;
